import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import store from "store2";
import { List, arrayMove } from "react-movable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAlignCenter,
  faAlignLeft,
  faAlignRight,
  faBold,
  faGripLines,
  faItalic,
  faLink,
  faListOl,
  faListUl,
  faPlus,
  faQuoteRight,
  faRedo,
  faStrikethrough,
  faTrash,
  faUnderline,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import {
  ajaxPerso,
  confirmation_modal,
  fetchShopifyBrands,
  handelize,
  meta,
} from "../fnc";

const refConst = require("../constants.js").v;
const MAX_PRODUCTS = 40;
const MAX_WARRANTY_SECTIONS = 24;
const LIST_TYPES = [
  { value: "new_items", label: "New Items", tag: "New Arrival" },
  { value: "top_sellers", label: "Top Sellers", tag: "Top Seller" },
];
const FEATURES = [
  {
    value: "product_lists",
    title: "Product rails",
    description: "Build the ordered New Items and Top Sellers carousels.",
    icon: "bi-list-stars",
  },
  {
    value: "vendor_dashboard",
    title: "Vendor dashboard",
    description: "Choose and order the category links in a vendor dashboard.",
    icon: "bi-grid-1x2-fill",
  },
  {
    value: "warranty_registration",
    title: "Warranty & Registration Information",
    description: "Build the vendor warranty accordion with rich, reusable content panels.",
    icon: "bi-shield-check",
  },
];
const selectStyles = { menu: (provided) => ({ ...provided, zIndex: 99999 }) };

const parseValue = (raw) => {
  if (!raw) return { products: [] };
  if (typeof raw === "object")
    return { products: Array.isArray(raw.products) ? raw.products : [] };
  try {
    const parsed = JSON.parse(raw);
    return { products: Array.isArray(parsed?.products) ? parsed.products : [] };
  } catch (e) {
    return { products: [] };
  }
};

const parseDashboardValue = (raw) => {
  if (!raw) return { items: [] };
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return { items: Array.isArray(parsed?.items) ? parsed.items : [] };
  } catch (e) {
    return { items: [] };
  }
};

const parseWarrantyValue = (raw) => {
  if (!raw) return { sections: [] };
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return {
      sections: Array.isArray(parsed?.sections)
        ? parsed.sections
            .filter((item) => item?.key && item?.title)
            .map((item) => ({
              key: String(item.key),
              title: String(item.title),
            }))
        : [],
    };
  } catch (e) {
    return { sections: [] };
  }
};

const createWarrantyKey = () =>
  `warranty_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

function EditorButton({ active, children, label, onClick }) {
  return (
    <button
      type="button"
      className={`btn btn-sm warranty-editor__button ${active ? "active" : ""}`}
      title={label}
      aria-label={label}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function WarrantyEditor({ content, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
      }),
    ],
    content: content || "<p></p>",
    editorProps: {
      attributes: {
        class: "warranty-editor__content",
        "aria-label": "Warranty section content",
      },
    },
    onUpdate: ({ editor: nextEditor }) => onChange(nextEditor.getHTML()),
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || "<p></p>", false);
    }
  }, [content, editor]);

  if (!editor) return <div className="warranty-editor__loading">Opening editor…</div>;

  const setLink = () => {
    const previous = editor.getAttributes("link").href || "";
    const href = window.prompt("Link URL", previous);
    if (href === null) return;
    if (!href.trim()) return editor.chain().focus().unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: href.trim() }).run();
  };

  return (
    <div className="warranty-editor">
      <div className="warranty-editor__toolbar" role="toolbar" aria-label="Rich text formatting">
        <EditorButton label="Undo" onClick={() => editor.chain().focus().undo().run()}><FontAwesomeIcon icon={faUndo} /></EditorButton>
        <EditorButton label="Redo" onClick={() => editor.chain().focus().redo().run()}><FontAwesomeIcon icon={faRedo} /></EditorButton>
        <span className="warranty-editor__divider" />
        <EditorButton label="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><FontAwesomeIcon icon={faBold} /></EditorButton>
        <EditorButton label="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><FontAwesomeIcon icon={faItalic} /></EditorButton>
        <EditorButton label="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><FontAwesomeIcon icon={faUnderline} /></EditorButton>
        <EditorButton label="Strike through" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><FontAwesomeIcon icon={faStrikethrough} /></EditorButton>
        <span className="warranty-editor__divider" />
        <EditorButton label="Heading" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</EditorButton>
        <EditorButton label="Subheading" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>H3</EditorButton>
        <EditorButton label="Bulleted list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><FontAwesomeIcon icon={faListUl} /></EditorButton>
        <EditorButton label="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><FontAwesomeIcon icon={faListOl} /></EditorButton>
        <EditorButton label="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><FontAwesomeIcon icon={faQuoteRight} /></EditorButton>
        <span className="warranty-editor__divider" />
        <EditorButton label="Align left" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}><FontAwesomeIcon icon={faAlignLeft} /></EditorButton>
        <EditorButton label="Align center" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}><FontAwesomeIcon icon={faAlignCenter} /></EditorButton>
        <EditorButton label="Align right" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}><FontAwesomeIcon icon={faAlignRight} /></EditorButton>
        <EditorButton label="Add or edit link" active={editor.isActive("link")} onClick={setLink}><FontAwesomeIcon icon={faLink} /></EditorButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

const productImage = (product) => {
  const images = product.images_json;
  if (typeof images === "string") return images;
  if (Array.isArray(images)) {
    const first = images[0];
    return typeof first === "string"
      ? first
      : first?.src || first?.url || first?.node?.url || "";
  }
  return images?.src || images?.url || images?.edges?.[0]?.node?.url || "";
};

export default function SpecialVendorNewItems(props) {
  if (!props.securityClearance(props)) return null;

  const stores = refConst.stores.filter((item) => item.id_marketplace == 14);
  const [shopSelected, setShopSelected] = useState(
    store("storeSelectedSpecialVendorNewItems") || stores[0] || null,
  );
  const [brands, setBrands] = useState([]);
  const [collections, setCollections] = useState([]);
  const [savedVendorLists, setSavedVendorLists] = useState([]);
  const [isSavedVendorListsLoading, setIsSavedVendorListsLoading] = useState(false);
  const [listType, setListType] = useState(LIST_TYPES[0]);
  const [vendor, setVendor] = useState(null);
  const [indexedCollectionId, setIndexedCollectionId] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [metaId, setMetaId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [feature, setFeature] = useState(FEATURES[0]);
  const [dashboardCollection, setDashboardCollection] = useState(null);
  const [dashboardItems, setDashboardItems] = useState([]);
  const [dashboardMetaId, setDashboardMetaId] = useState(null);
  const [dashboardDirty, setDashboardDirty] = useState(false);
  const [dashboardCategoryOptions, setDashboardCategoryOptions] = useState([]);
  const [isDashboardCategoryOptionsLoading, setIsDashboardCategoryOptionsLoading] = useState(false);
  const [warrantyCollection, setWarrantyCollection] = useState(null);
  const [warrantySections, setWarrantySections] = useState([]);
  const [warrantyMasterId, setWarrantyMasterId] = useState(null);
  const [warrantyDeletedKeys, setWarrantyDeletedKeys] = useState([]);
  const [selectedWarrantyKey, setSelectedWarrantyKey] = useState(null);
  const [warrantyDirty, setWarrantyDirty] = useState(false);
  const [isWarrantyLoading, setIsWarrantyLoading] = useState(false);
  const [savedWarrantyCollections, setSavedWarrantyCollections] = useState([]);
  const [isSavedWarrantyCollectionsLoading, setIsSavedWarrantyCollectionsLoading] = useState(false);

  const vendorCollection = useMemo(() => {
    if (!vendor) return null;
    const target = handelize(vendor);
    return (
      collections.find(
        (collection) =>
          String(collection.id) === String(indexedCollectionId || "") ||
          handelize(collection.title) === target ||
          collection.handle === target,
      ) || null
    );
  }, [vendor, collections, indexedCollectionId]);

  const vendorOptions = useMemo(() => {
    const saved = savedVendorLists.map((item) => ({
      value: item.vendor,
      label: item.vendor,
      collectionId: item.collection_id,
    }));
    const savedNames = new Set(saved.map((item) => handelize(item.value)));
    const otherBrands = brands
      .filter((name) => !savedNames.has(handelize(name)))
      .map((name) => ({ value: name, label: name }));
    return [
      ...(saved.length ? [{ label: `Saved ${listType.label} lists`, options: saved }] : []),
      ...(otherBrands.length ? [{ label: "Other Shopify vendors", options: otherBrands }] : []),
    ];
  }, [brands, listType.label, savedVendorLists]);

  const productOptions = useMemo(
    () =>
      products.map((product) => ({
        value: product.handle,
        label: `${product.title}${product.sku ? ` — ${product.sku}` : ""}`,
        product,
      })),
    [products],
  );

  const dashboardCategoryChoices = useMemo(
    () =>
      dashboardCategoryOptions
        .map((item) => ({
          ...item,
          collection: collections.find((collection) => collection.handle === item.handle),
        }))
        .filter((item) => item.collection),
    [collections, dashboardCategoryOptions],
  );

  const warrantyCollectionOptions = useMemo(() => {
    const vendorCollectionIds = new Set(
      collections
        .filter((collection) =>
          brands.some((brand) => handelize(brand) === handelize(collection.title)),
        )
        .map((collection) => String(collection.id)),
    );
    const savedIds = new Set(savedWarrantyCollections.map((item) => item.collection_id));
    const saved = collections
      .filter((collection) =>
        vendorCollectionIds.has(String(collection.id)) && savedIds.has(String(collection.id)),
      )
      .map((collection) => ({
        value: collection.id,
        label: `${collection.title} — /collections/${collection.handle}`,
        collection,
      }));
    const other = collections
      .filter((collection) =>
        vendorCollectionIds.has(String(collection.id)) && !savedIds.has(String(collection.id)),
      )
      .map((collection) => ({
        value: collection.id,
        label: `${collection.title} — /collections/${collection.handle}`,
        collection,
      }));
    saved.sort((a, b) => a.label.localeCompare(b.label));
    other.sort((a, b) => a.label.localeCompare(b.label));
    return [
      ...(saved.length ? [{ label: "Warranty information already configured", options: saved }] : []),
      ...(other.length ? [{ label: "Other Shopify collections", options: other }] : []),
    ];
  }, [brands, collections, savedWarrantyCollections]);

  useEffect(() => {
    if (!shopSelected?.id) return;
    setVendor(null);
    setIndexedCollectionId(null);
    setProducts([]);
    setSelectedProducts([]);
    setMetaId(null);
    setDirty(false);
    setDashboardCollection(null);
    setDashboardItems([]);
    setDashboardMetaId(null);
    setDashboardDirty(false);
    setDashboardCategoryOptions([]);
    setWarrantyCollection(null);
    setWarrantySections([]);
    setWarrantyMasterId(null);
    setWarrantyDeletedKeys([]);
    setSelectedWarrantyKey(null);
    setWarrantyDirty(false);
    setSavedWarrantyCollections([]);
    loadCollections();
    fetchShopifyBrands(shopSelected.id, null, null, (response) => {
      const names =
        response?.success && Array.isArray(response.data)
          ? response.data
              .map((row) => row?.vendor || row?.name || row)
              .filter((name) => typeof name === "string" && name.trim())
          : [];
      setBrands([...new Set(names)].sort((a, b) => a.localeCompare(b)));
    });
  }, [shopSelected]);

  useEffect(() => {
    if (!dashboardCollection?.id || !shopSelected?.id) return;
    loadDashboardCategories();
    loadDashboardCategoryOptions();
  }, [shopSelected?.id, dashboardCollection?.id]);

  useEffect(() => {
    if (!warrantyCollection?.id || !shopSelected?.id) return;
    loadWarrantyRegistration();
  }, [shopSelected?.id, warrantyCollection?.id]);

  useEffect(() => {
    if (!shopSelected?.id || !listType?.value) return undefined;
    let active = true;
    setIsSavedVendorListsLoading(true);
    ajaxPerso(
      {
        api: "shopify",
        trigger: "get_special_vendor_index",
        shop: shopSelected.id,
      },
      (response) => {
        if (!active) return;
        setIsSavedVendorListsLoading(false);
        if (!response?.success) {
          setSavedVendorLists([]);
          return props.info({
            error: response?.errors || "Unable to load saved vendor lists.",
          });
        }
        const seen = new Set();
        setSavedVendorLists(
          (response.data?.lists || []).reduce((items, row) => {
            if (
              row?.list_key !== listType.value ||
              !row?.vendor ||
              !row?.collection_id
            )
              return items;
            const key = `${row.collection_id}:${row.list_key}`;
            if (seen.has(key)) return items;
            seen.add(key);
            items.push({
              vendor: row.vendor,
              collection_id: String(row.collection_id),
            });
            return items;
          }, []),
        );
      },
    );
    return () => {
      active = false;
    };
  }, [shopSelected?.id, listType.value]);

  useEffect(() => {
    if (!shopSelected?.id) return undefined;
    let active = true;
    setIsSavedWarrantyCollectionsLoading(true);
    ajaxPerso(
      { api: "shopify", trigger: "get_special_vendor_warranty_index", shop: shopSelected.id },
      (response) => {
        if (!active) return;
        setIsSavedWarrantyCollectionsLoading(false);
        if (!response?.success) {
          setSavedWarrantyCollections([]);
          return props.info({ error: response?.errors || "Unable to load saved warranty collections." });
        }
        setSavedWarrantyCollections(
          (response.data?.registrations || [])
            .filter((item) => item?.collection_id)
            .map((item) => ({ collection_id: String(item.collection_id), vendor: item.vendor || "" })),
        );
      },
    );
    return () => { active = false; };
  }, [shopSelected?.id]);

  useEffect(() => {
    if (!shopSelected?.id || !vendorCollection || !vendor) return;
    loadProducts();
    loadSavedProducts();
  }, [shopSelected, vendor, vendorCollection?.id, listType.value]);

  const loadCollections = () => {
    props.loading(true);
    ajaxPerso(
      { api: "shopify", trigger: "downloadCollection", shop: shopSelected.id },
      (response) => {
        props.loading(false);
        if (!response?.success)
          return props.info({
            error: response?.error || "Unable to load Shopify collections.",
          });
        setCollections(
          (response.data || [])
            .map((row) => ({
              id: String(row.id || row.legacyResourceId || ""),
              handle: row.handle || "",
              title: row.title || row.handle || "",
            }))
            .filter((row) => row.id && row.handle),
        );
      },
    );
  };

  const loadProducts = () => {
    props.loading(true);
    ajaxPerso(
      {
        api: "shopify",
        trigger: "getItems",
        shop: shopSelected.id,
        vendor,
        tag: listType.tag,
        nbrItem: 500,
      },
      (response) => {
        props.loading(false);
        if (!response?.success)
          return props.info({
            error: response?.error || "Unable to load this vendor’s products.",
          });
        const seen = new Set();
        setProducts(
          (response.data || [])
            .reduce((list, row) => {
              const handle = String(row.handle || "").trim();
              if (!handle || seen.has(handle)) return list;
              seen.add(handle);
              list.push({
                id: String(row.id_product_shopify || row.id || ""),
                handle,
                title: row.title || handle,
                sku: row.sku || "",
                image: productImage(row),
              });
              return list;
            }, [])
            .sort((a, b) => a.title.localeCompare(b.title)),
        );
      },
    );
  };

  const loadSavedProducts = () => {
    props.loading(true);
    meta.fetch(
      {
        shop: shopSelected.id,
        namespace: "special_vendor",
        key: listType.value,
        urlPrefix: `collections/${vendorCollection.id}/`,
      },
      (response) => {
        props.loading(false);
        if (response?.success && response.res?.length) {
          const row = response.res[0];
          setMetaId(row.id);
          setSelectedProducts(
            parseValue(row.value).products.map((product, index) => ({
              ...product,
              _key: `saved_${index}_${product.handle}`,
            })),
          );
        } else {
          setMetaId(null);
          setSelectedProducts([]);
        }
        setDirty(false);
      },
    );
  };

  const addProduct = (option) => {
    const product = option?.product;
    if (!product) return;
    if (selectedProducts.some((item) => item.handle === product.handle))
      return props.info({ error: "This product is already in the list." });
    if (selectedProducts.length >= MAX_PRODUCTS)
      return props.info({
        error: `A list may contain up to ${MAX_PRODUCTS} products.`,
      });
    setSelectedProducts((current) => [
      ...current,
      { ...product, _key: `${Date.now()}_${product.handle}` },
    ]);
    setDirty(true);
  };

  const removeProduct = (key) => {
    setSelectedProducts((current) =>
      current.filter((product) => product._key !== key),
    );
    setDirty(true);
  };

  const save = () => {
    if (!vendor || !vendorCollection) return;
    if (!selectedProducts.length)
      return props.info({ error: "Add at least one product before saving." });
    props.loading(true);
    ajaxPerso(
      {
        api: "shopify",
        trigger: "set_special_vendor_new_items",
        uid: props.getUser().uid,
        token: refConst.tokenEio,
        shop: shopSelected.id,
        collectionId: vendorCollection.id,
        vendor,
        listKey: listType.value,
        products: JSON.stringify(
          selectedProducts.map(({ _key, ...product }) => product),
        ),
      },
      (response) => {
        props.loading(false);
        if (response?.errors) return props.info({ error: response.errors });
        if (!response?.success)
          return props.info({ error: `Could not save the ${listType.label} list.` });
        setMetaId(response.data?.metafieldId || metaId);
        setDirty(false);
        // The API has already updated the shop-level index; refresh the local
        // selector so the new list is immediately available after a switch.
        setSavedVendorLists((current) => {
          const withoutCurrent = current.filter(
            (item) =>
              item.collection_id !== String(vendorCollection.id),
          );
          return [
            ...withoutCurrent,
            { vendor, collection_id: String(vendorCollection.id) },
          ];
        });
        props.info({
          success: `Saved ${selectedProducts.length} ordered ${listType.label} for ${vendor}.`,
        });
      },
    );
  };

  const deleteMeta = () =>
    confirmation_modal(
      props,
      () => {
        props.loading(true);
        ajaxPerso(
          {
            api: "shopify",
            trigger: "set_special_vendor_new_items",
            uid: props.getUser().uid,
            token: refConst.tokenEio,
            shop: shopSelected.id,
            collectionId: vendorCollection.id,
            vendor,
            listKey: listType.value,
            clear: true,
          },
          (response) => {
            props.loading(false);
            if (response?.errors) return props.info({ error: response.errors });
            if (!response?.success)
              return props.info({
                error: `Could not delete the ${listType.label} metafield.`,
              });
            setMetaId(null);
            setSelectedProducts([]);
            setDirty(false);
            setSavedVendorLists((current) =>
              current.filter(
                (item) => item.collection_id !== String(vendorCollection.id),
              ),
            );
            props.info({ success: `${listType.label} metafield removed.` });
          },
        );
      },
      null,
      <div className="mt-2">
        Remove the complete {listType.label} list for <strong>{vendor}</strong>?
      </div>,
    );

  const loadDashboardCategories = () => {
    if (!dashboardCollection) return;
    props.loading(true);
    meta.fetch(
      {
        shop: shopSelected.id,
        namespace: "special_vendor",
        key: "dashboard_categories",
        urlPrefix: `collections/${dashboardCollection.id}/`,
      },
      (response) => {
        props.loading(false);
        if (response?.success && response.res?.length) {
          const row = response.res[0];
          const items = parseDashboardValue(row.value).items;
          setDashboardMetaId(row.id);
          setDashboardItems(
            items.map((item, index) => ({
              handle: item.handle || "",
              title: item.title || "",
              _key: `dashboard_${index}_${item.handle}`,
            })),
          );
        } else {
          setDashboardMetaId(null);
          setDashboardItems([]);
        }
        setDashboardDirty(false);
      },
    );
  };

  const loadDashboardCategoryOptions = () => {
    if (!dashboardCollection) return;
    setIsDashboardCategoryOptionsLoading(true);
    ajaxPerso(
      {
        api: "shopify",
        trigger: "get_special_vendor_dashboard_category_options",
        shop: shopSelected.id,
        collectionId: dashboardCollection.id,
      },
      (response) => {
        setIsDashboardCategoryOptionsLoading(false);
        if (!response?.success) {
          setDashboardCategoryOptions([]);
          return props.info({ error: response?.errors || "Unable to load this vendor’s Shop by Category list." });
        }
        setDashboardCategoryOptions(
          (response.data?.items || []).filter((item) => item?.handle && item?.title),
        );
      },
    );
  };

  const toggleDashboardCategory = (category) => {
    const selected = dashboardItems.some((item) => item.handle === category.handle);
    if (!selected && dashboardItems.length >= MAX_PRODUCTS)
      return props.info({ error: `A dashboard can contain up to ${MAX_PRODUCTS} categories.` });
    setDashboardItems((current) =>
      selected
        ? current.filter((item) => item.handle !== category.handle)
        : [
            ...current,
            {
              handle: category.handle,
              type_handle: category.handle,
              title: category.title,
              _key: `${Date.now()}_${category.handle}`,
            },
          ],
    );
    setDashboardDirty(true);
  };

  const saveDashboardCategories = () => {
    if (!dashboardCollection || !dashboardItems.length) return;
    props.loading(true);
    ajaxPerso(
      {
        api: "shopify",
        trigger: "set_special_vendor_dashboard_categories",
        uid: props.getUser().uid,
        token: refConst.tokenEio,
        shop: shopSelected.id,
        collectionId: dashboardCollection.id,
        items: JSON.stringify(dashboardItems.map(({ _key, ...item }) => item)),
      },
      (response) => {
        props.loading(false);
        if (response?.errors) return props.info({ error: response.errors });
        if (!response?.success) return props.info({ error: "Could not save dashboard categories." });
        setDashboardMetaId(response.data?.metafieldId || dashboardMetaId);
        setDashboardDirty(false);
        props.info({ success: `Saved ${dashboardItems.length} dashboard categories for ${dashboardCollection.title}.` });
      },
    );
  };

  const deleteDashboardCategories = () =>
    confirmation_modal(
      props,
      () => {
        props.loading(true);
        ajaxPerso(
          {
            api: "shopify", trigger: "set_special_vendor_dashboard_categories",
            uid: props.getUser().uid, token: refConst.tokenEio, shop: shopSelected.id,
            collectionId: dashboardCollection.id, clear: true,
          },
          (response) => {
            props.loading(false);
            if (response?.errors || !response?.success)
              return props.info({ error: response?.errors || "Could not delete dashboard categories." });
            setDashboardMetaId(null); setDashboardItems([]); setDashboardDirty(false);
            props.info({ success: "Dashboard categories metafield removed." });
          },
        );
      }, null,
      <div className="mt-2">Remove every dashboard category from <strong>{dashboardCollection?.title}</strong>?</div>,
    );

  const loadWarrantyRegistration = () => {
    if (!warrantyCollection) return;
    setIsWarrantyLoading(true);
    props.loading(true);
    meta.fetch(
      {
        shop: shopSelected.id,
        namespace: "special_vendor",
        key: "warranty_registration",
        urlPrefix: `collections/${warrantyCollection.id}/`,
      },
      (response) => {
        if (!response?.success || !response.res?.length) {
          setWarrantyMasterId(null);
          setWarrantySections([]);
          setWarrantyDeletedKeys([]);
          setSelectedWarrantyKey(null);
          setWarrantyDirty(false);
          setIsWarrantyLoading(false);
          props.loading(false);
          return;
        }

        const row = response.res[0];
        const parsed = parseWarrantyValue(row.value);
        const sections = parsed.sections.map((section, index) => ({
          ...section,
          content: "",
          _key: `warranty_${index}_${section.key}`,
        }));
        setWarrantyMasterId(row.id);
        setWarrantyDeletedKeys([]);
        setWarrantySections(sections);
        setSelectedWarrantyKey(sections[0]?.key || null);
        setWarrantyDirty(false);

        if (!sections.length) {
          setIsWarrantyLoading(false);
          props.loading(false);
          return;
        }

        let pending = sections.length;
        const loadedSections = [...sections];
        sections.forEach((section, index) => {
          meta.fetch(
            {
              shop: shopSelected.id,
              namespace: "special_vendor",
              key: section.key,
              urlPrefix: `collections/${warrantyCollection.id}/`,
            },
            (contentResponse) => {
              const contentRow = contentResponse?.success && contentResponse.res?.length
                ? contentResponse.res[0]
                : null;
              loadedSections[index] = {
                ...loadedSections[index],
                content: String(contentRow?.value || ""),
              };
              pending -= 1;
              if (pending === 0) {
                setWarrantySections(loadedSections);
                setIsWarrantyLoading(false);
                props.loading(false);
              }
            },
          );
        });
      },
    );
  };

  const addWarrantySection = () => {
    if (warrantySections.length >= MAX_WARRANTY_SECTIONS)
      return props.info({ error: `A warranty accordion may contain up to ${MAX_WARRANTY_SECTIONS} sections.` });
    const key = createWarrantyKey();
    setWarrantySections((current) => [
      ...current,
      { key, title: "", content: "<p></p>", _key: `${Date.now()}_${key}` },
    ]);
    setSelectedWarrantyKey(key);
    setWarrantyDirty(true);
  };

  const updateWarrantySection = (key, updates) => {
    setWarrantySections((current) =>
      current.map((section) => section.key === key ? { ...section, ...updates } : section),
    );
    setWarrantyDirty(true);
  };

  const removeWarrantySection = (section) => {
    setWarrantySections((current) => current.filter((item) => item.key !== section.key));
    if (warrantyMasterId) {
      setWarrantyDeletedKeys((current) =>
        current.includes(section.key) ? current : [...current, section.key],
      );
    }
    setSelectedWarrantyKey((current) => current === section.key ? null : current);
    setWarrantyDirty(true);
  };

  const saveWarrantyRegistration = () => {
    if (!warrantyCollection) return;
    if (!warrantySections.length)
      return props.info({ error: "Add at least one warranty or registration section before saving." });
    const incomplete = warrantySections.find(
      (section) => !section.title.trim() || !section.content.replace(/<[^>]*>/g, "").trim(),
    );
    if (incomplete)
      return props.info({ error: "Every warranty section needs a title and some content." });

    props.loading(true);
    ajaxPerso(
      {
        api: "shopify",
        trigger: "set_special_vendor_warranty_registration",
        uid: props.getUser().uid,
        token: refConst.tokenEio,
        shop: shopSelected.id,
        collectionId: warrantyCollection.id,
        vendor: warrantyCollection.title,
        sections: JSON.stringify(warrantySections.map(({ _key, ...section }) => section)),
        removedKeys: JSON.stringify(warrantyDeletedKeys),
      },
      (response) => {
        props.loading(false);
        if (response?.errors || !response?.success)
          return props.info({ error: response?.errors || "Could not save the warranty information." });
        setWarrantyMasterId(response.data?.metafieldId || warrantyMasterId);
        setWarrantyDeletedKeys([]);
        setWarrantyDirty(false);
        setSavedWarrantyCollections((current) => [
          ...current.filter((item) => item.collection_id !== String(warrantyCollection.id)),
          { collection_id: String(warrantyCollection.id), vendor: warrantyCollection.title },
        ]);
        props.info({ success: `Saved ${warrantySections.length} warranty and registration sections for ${warrantyCollection.title}.` });
      },
    );
  };

  const deleteWarrantyRegistration = () =>
    confirmation_modal(
      props,
      () => {
        props.loading(true);
        ajaxPerso(
          {
            api: "shopify",
            trigger: "set_special_vendor_warranty_registration",
            uid: props.getUser().uid,
            token: refConst.tokenEio,
            shop: shopSelected.id,
            collectionId: warrantyCollection.id,
            vendor: warrantyCollection.title,
            clear: true,
          },
          (response) => {
            props.loading(false);
            if (response?.errors || !response?.success)
              return props.info({ error: response?.errors || "Could not delete the warranty information." });
            setWarrantyMasterId(null);
            setWarrantySections([]);
            setWarrantyDeletedKeys([]);
            setSelectedWarrantyKey(null);
            setWarrantyDirty(false);
            setSavedWarrantyCollections((current) =>
              current.filter((item) => item.collection_id !== String(warrantyCollection.id)),
            );
            props.info({ success: "Warranty & Registration Information removed." });
          },
        );
      },
      null,
      <div className="mt-2">Remove the complete warranty and registration accordion for <strong>{warrantyCollection?.title}</strong>?</div>,
    );

  return (
    <div className="main ctnPage container-fluid">
      <h1 className="page-header mb-2">Theme Features</h1>
      <p className="text-muted mb-4">
        Open a storefront block, then configure exactly the content that block reads.
      </p>
      <div className="row mb-4">
        {FEATURES.map((item) => (
          <div className="col-md-6 col-xl-4 mb-3" key={item.value}>
            <button
              type="button"
              className={`w-100 h-100 text-left border rounded p-4 bg-white ${feature.value === item.value ? "border-primary shadow-sm" : ""}`}
              onClick={() => {
                if (dirty || dashboardDirty || warrantyDirty) return props.info({ error: "Save or reload the current changes before changing feature." });
                setFeature(item);
              }}
            >
              <div className="d-flex align-items-center">
                <i className={`bi ${item.icon} text-primary mr-3`} style={{ fontSize: "2rem" }} />
                <span><span className="font-weight-bold d-block">{item.title}</span><span className="small text-muted">{item.description}</span></span>
              </div>
            </button>
          </div>
        ))}
      </div>
      {feature.value === "product_lists" ? <div className="p-3 bg-white border mb-3">
        <label className="font-weight-bolder d-block">List type</label>
        <p className="small text-muted">Choose the section before choosing its vendor.</p>
        <Select
          styles={selectStyles}
          options={LIST_TYPES}
          value={listType}
          isDisabled={dirty || dashboardDirty || warrantyDirty}
          onChange={(option) => {
            setSelectedProducts([]);
            setMetaId(null);
            setVendor(null);
            setIndexedCollectionId(null);
            setListType(option || LIST_TYPES[0]);
          }}
        />
        {dirty ? <div className="small text-warning mt-2">Save or reload the current list before changing type.</div> : null}
      </div> : null}
      <div className="p-3 bg-light border mb-3">
        <label className="font-weight-bolder d-block">Store</label>
        <Select
          styles={selectStyles}
          className="w-50"
          isDisabled={dirty || dashboardDirty || warrantyDirty}
          options={stores.map((item) => ({
            value: item.id,
            label: item.name,
            store: item,
          }))}
          value={
            shopSelected
              ? { value: shopSelected.id, label: shopSelected.name }
              : null
          }
          onChange={(option) => {
            store("storeSelectedSpecialVendorNewItems", option.store);
            setShopSelected(option.store);
          }}
        />
        {dirty || dashboardDirty || warrantyDirty ? (
          <div className="small text-warning mt-2">
            Save or reload the current feature before changing stores.
          </div>
        ) : null}
      </div>
      {feature.value === "vendor_dashboard" ? (
        <>
          <div className="p-3 bg-white border mb-3">
            <label className="font-weight-bolder d-block">Dashboard collection</label>
            <p className="small text-muted">Choose the vendor collection assigned as <code>source_collection</code> in the dashboard block. Its current Shop by Category types are loaded below.</p>
            <Select
              styles={selectStyles}
              placeholder="Select the vendor dashboard collection..."
              options={collections.map((collection) => ({ value: collection.id, label: `${collection.title} — /collections/${collection.handle}`, collection })).sort((a, b) => a.label.localeCompare(b.label))}
              value={dashboardCollection ? { value: dashboardCollection.id, label: `${dashboardCollection.title} — /collections/${dashboardCollection.handle}` } : null}
              isDisabled={dashboardDirty}
              onChange={(option) => {
                if (dashboardDirty) return props.info({ error: "Save or reload the dashboard before changing collection." });
                setDashboardCollection(option?.collection || null);
              }}
            />
            {dashboardCollection ? <div className="small text-muted mt-2">Saving to <code>/collections/{dashboardCollection.handle}</code>{dashboardMetaId ? <span className="badge badge-success ml-2">Categories saved</span> : null}{dashboardDirty ? <span className="badge badge-warning ml-2">Unsaved changes</span> : null}</div> : null}
          </div>
          {dashboardCollection ? <>
            <div className="p-3 bg-light border mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="font-weight-bolder mb-0">Shop by Category</label>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadDashboardCategoryOptions} disabled={isDashboardCategoryOptionsLoading}>Refresh categories</button>
              </div>
              <div className="small text-muted mb-3">These are the product types present in <code>{dashboardCollection.handle}</code> that also have a matching Shopify category collectio, the same rule used on the current vendor page. Check the ones that belong in this dashboard.</div>
              {isDashboardCategoryOptionsLoading ? <div className="small text-primary d-flex align-items-center"><span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />Loading categories…</div> : dashboardCategoryChoices.length ? (
                <div className="row">
                  {dashboardCategoryChoices.map((category) => {
                    const checked = dashboardItems.some((item) => item.handle === category.handle);
                    return <div className="col-md-6 mb-2" key={category.handle}>
                      <label className={`d-flex align-items-center border rounded p-2 mb-0 bg-white ${checked ? "border-primary" : ""}`}>
                        <input type="checkbox" className="mr-2" checked={checked} onChange={() => toggleDashboardCategory(category)} />
                        <span><span className="font-weight-bold d-block">{category.title}</span><span className="small text-muted">/collections/{dashboardCollection.handle}/category_{category.handle}</span></span>
                      </label>
                    </div>;
                  })}
                </div>
              ) : <div className="alert alert-secondary mb-0">No matching Shop by Category entries were found. A category appears only when this vendor has products with that type and Shopify has a collection with the same handle.</div>}
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Category order <span className="text-muted small">({dashboardItems.length}/{MAX_PRODUCTS})</span></h5>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadDashboardCategories}>Reload saved categories</button>
            </div>
            {dashboardItems.length === 0 ? <div className="alert alert-secondary">No categories selected yet. Add the collections the dashboard should show.</div> : (
              <List
                values={dashboardItems}
                onChange={({ oldIndex, newIndex }) => { setDashboardItems((current) => arrayMove(current, oldIndex, newIndex)); setDashboardDirty(true); }}
                renderList={({ children, props: listProps }) => <div {...listProps} className="mb-4">{children}</div>}
                renderItem={({ value, props: itemProps, isDragged }) => (
                  <div {...itemProps} key={value._key} className="border rounded bg-white p-2 mb-2 d-flex align-items-center" style={{ ...itemProps.style, opacity: isDragged ? 0.8 : 1 }}>
                    <span className="text-muted px-2"><FontAwesomeIcon icon={faGripLines} /></span>
                    <div className="flex-grow-1 mr-3"><div className="small text-muted mb-1"><code>{value.type_handle ? `/collections/${dashboardCollection.handle}/category_${value.type_handle}` : `/collections/${value.handle}`}</code></div><input className="form-control form-control-sm" value={value.title} placeholder="Optional storefront title" onChange={(event) => { const title = event.target.value; setDashboardItems((current) => current.map((item) => item._key === value._key ? { ...item, title } : item)); setDashboardDirty(true); }} onClick={(event) => event.stopPropagation()} /></div>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={(event) => { event.stopPropagation(); setDashboardItems((current) => current.filter((item) => item._key !== value._key)); setDashboardDirty(true); }} title="Remove"><FontAwesomeIcon icon={faTrash} /></button>
                  </div>
                )}
              />
            )}
            <div className="d-flex align-items-center mb-5">
              <button type="button" className="btn btn-success px-4" disabled={!dashboardItems.length || props.loading()} onClick={saveDashboardCategories}><FontAwesomeIcon icon={faPlus} className="mr-1" />Save dashboard categories</button>
              <button type="button" className="btn btn-outline-danger ml-2" disabled={!dashboardMetaId || props.loading()} onClick={deleteDashboardCategories}>Delete metafield</button>
              <div className="small text-muted ml-3">Theme reads <code>collection.metafields.special_vendor.dashboard_categories.value.items</code> in this exact order.</div>
            </div>
          </> : null}
        </>
      ) : null}
      {feature.value === "warranty_registration" ? (
        <>
          <div className="warranty-panel mb-3">
            <label className="font-weight-bolder d-block">Vendor collection</label>
            <p className="small text-muted mb-2">Saved vendor collections are listed first. The small master metafield tracks accordion order and titles; each section’s rich content is stored in its own collection metafield.</p>
            <Select
              styles={selectStyles}
              placeholder="Select a vendor collection..."
              isDisabled={warrantyDirty || isSavedWarrantyCollectionsLoading}
              isLoading={isSavedWarrantyCollectionsLoading}
              noOptionsMessage={() => "No collection title matches a Shopify vendor name."}
              options={warrantyCollectionOptions}
              value={warrantyCollection ? {
                value: warrantyCollection.id,
                label: `${warrantyCollection.title} — /collections/${warrantyCollection.handle}`,
              } : null}
              onChange={(option) => {
                if (warrantyDirty) return props.info({ error: "Save or reload the warranty information before changing collection." });
                setWarrantyCollection(option?.collection || null);
              }}
            />
            {warrantyCollection ? <div className="small text-muted mt-2">Storefront source: <code>/collections/{warrantyCollection.handle}</code>{warrantyMasterId ? <span className="badge badge-success ml-2">Information saved</span> : <span className="badge badge-secondary ml-2">Not configured yet</span>}{warrantyDirty ? <span className="badge badge-warning ml-2">Unsaved changes</span> : null}</div> : null}
          </div>
          {warrantyCollection ? (
            <>
              <div className="warranty-panel warranty-panel--sections mb-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                  <div>
                    <h5 className="mb-1">Accordion sections <span className="text-muted small">({warrantySections.length}/{MAX_WARRANTY_SECTIONS})</span></h5>
                    <div className="small text-muted">Drag to set storefront order. Open a section to edit its title and formatted content.</div>
                  </div>
                  <div className="mt-2 mt-sm-0">
                    <button type="button" className="btn btn-sm btn-outline-secondary mr-2" disabled={isWarrantyLoading} onClick={loadWarrantyRegistration}>Reload</button>
                    <button type="button" className="btn btn-sm btn-primary" disabled={isWarrantyLoading || warrantySections.length >= MAX_WARRANTY_SECTIONS} onClick={addWarrantySection}><FontAwesomeIcon icon={faPlus} className="mr-1" />Add section</button>
                  </div>
                </div>
                {isWarrantyLoading ? <div className="warranty-editor__loading"><span className="spinner-border spinner-border-sm mr-2" aria-hidden="true" />Loading warranty sections…</div> : !warrantySections.length ? <div className="alert alert-secondary mb-0">No information is saved for this vendor yet. Add the first accordion section to begin.</div> : (
                  <List
                    values={warrantySections}
                    onChange={({ oldIndex, newIndex }) => { setWarrantySections((current) => arrayMove(current, oldIndex, newIndex)); setWarrantyDirty(true); }}
                    renderList={({ children, props: listProps }) => <div {...listProps}>{children}</div>}
                    renderItem={({ value, props: itemProps, isDragged }) => {
                      const isOpen = selectedWarrantyKey === value.key;
                      return <div {...itemProps} key={value._key} className={`warranty-section mb-2 ${isOpen ? "warranty-section--open" : ""}`} style={{ ...itemProps.style, opacity: isDragged ? 0.8 : 1 }}>
                        <div className="warranty-section__header">
                          <span className="warranty-section__drag" data-movable-handle title="Drag this section to reorder"><FontAwesomeIcon icon={faGripLines} /></span>
                          <button type="button" className="warranty-section__toggle" onClick={(event) => { event.stopPropagation(); setSelectedWarrantyKey(isOpen ? null : value.key); }} aria-expanded={isOpen}>
                            <span className="warranty-section__plus" aria-hidden="true">{isOpen ? "−" : "+"}</span>
                            <span>{value.title || "Untitled section"}</span>
                          </button>
                          <button type="button" className="btn btn-sm btn-outline-danger ml-2" title="Remove section" onClick={(event) => { event.stopPropagation(); removeWarrantySection(value); }}><FontAwesomeIcon icon={faTrash} /></button>
                        </div>
                        {isOpen ? <div className="warranty-section__body" onClick={(event) => event.stopPropagation()}>
                          <label className="font-weight-bold small" htmlFor={`warranty-title-${value.key}`}>Accordion title</label>
                          <input id={`warranty-title-${value.key}`} className="form-control mb-3" value={value.title} maxLength={255} placeholder="e.g. Airless Paint Sprayer (M4910-20)" onChange={(event) => updateWarrantySection(value.key, { title: event.target.value })} />
                          <label className="font-weight-bold small d-block">Content</label>
                          <WarrantyEditor content={value.content} onChange={(content) => updateWarrantySection(value.key, { content })} />
                        </div> : null}
                      </div>;
                    }}
                  />
                )}
              </div>
              <div className="d-flex flex-wrap align-items-center mb-4">
                <button type="button" className="btn btn-primary mr-2 mb-2" disabled={!warrantyDirty || isWarrantyLoading} onClick={saveWarrantyRegistration}>Save Warranty & Registration Information</button>
                {warrantyMasterId ? <button type="button" className="btn btn-outline-danger mb-2" disabled={isWarrantyLoading} onClick={deleteWarrantyRegistration}>Delete all information</button> : null}
                {warrantyDirty ? <span className="small text-warning ml-2 mb-2">Your changes have not been saved.</span> : null}
              </div>
            </>
          ) : null}
        </>
      ) : null}
      {feature.value === "product_lists" ? <>
      <div className="p-3 bg-white border mb-3">
        <label className="font-weight-bolder d-block">Vendor</label>
        <p className="small text-muted">
          Only products assigned to the chosen Shopify vendor are available.
        </p>
        {isSavedVendorListsLoading ? (
          <div className="small text-primary mb-2 d-flex align-items-center">
            <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
            Loading saved {listType.label.toLowerCase()} lists…
          </div>
        ) : savedVendorLists.length ? (
          <div className="small text-success mb-2">
            {savedVendorLists.length} saved {listType.label.toLowerCase()} list{savedVendorLists.length === 1 ? "" : "s"} ready to open.
          </div>
        ) : (
          <div className="small text-muted mb-2">No saved {listType.label.toLowerCase()} lists for this store yet.</div>
        )}
        <Select
          styles={selectStyles}
          placeholder="Select vendor..."
          options={vendorOptions}
          isLoading={isSavedVendorListsLoading}
          isDisabled={isSavedVendorListsLoading}
          value={vendor ? { value: vendor, label: vendor } : null}
          onChange={(option) => {
            if (dirty)
              return props.info({
                error:
                  "Save or reload your current changes before changing vendor.",
              });
            setVendor(option?.value || null);
            setIndexedCollectionId(option?.collectionId || null);
          }}
        />
        {vendor && !vendorCollection ? (
          <div className="alert alert-warning mt-3 mb-0">
            No collection matches this vendor. Create or rename its special
            vendor collection so its title or handle matches{" "}
            <code>{handelize(vendor)}</code>.
          </div>
        ) : null}
        {vendorCollection ? (
          <div className="small text-muted mt-2">
            Saving to <code>/collections/{vendorCollection.handle}</code>
            {metaId ? (
              <span className="badge badge-success ml-2">List saved</span>
            ) : null}
            {dirty ? (
              <span className="badge badge-warning ml-2">Unsaved changes</span>
            ) : null}
          </div>
        ) : null}
      </div>
      {vendorCollection ? (
        <>
          <div className="p-3 bg-light border mb-3">
            <label className="font-weight-bolder d-block">Add {listType.label} product</label>
            <Select
              styles={selectStyles}
              placeholder={`Search this vendor’s ${listType.tag} products...`}
              options={productOptions.filter(
                (option) =>
                  !selectedProducts.some(
                    (item) => item.handle === option.value,
                  ),
              )}
              onChange={addProduct}
              value={null}
              isDisabled={products.length === 0}
            />
            <div className="small text-muted mt-2">
              {products.length
                ? `${products.length} products tagged “${listType.tag}” loaded. Add them, then drag the list into storefront order.`
                : `No products tagged “${listType.tag}” found for this vendor.`}
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">
              Display order{" "}
              <span className="text-muted small">
                ({selectedProducts.length}/{MAX_PRODUCTS})
              </span>
            </h5>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={loadSavedProducts}
            >
              Reload saved list
            </button>
          </div>
          {selectedProducts.length === 0 ? (
            <div className="alert alert-secondary">
              No products selected yet. Add a product above to start this {listType.label} rail.
            </div>
          ) : (
            <List
              values={selectedProducts}
              onChange={({ oldIndex, newIndex }) => {
                setSelectedProducts((current) =>
                  arrayMove(current, oldIndex, newIndex),
                );
                setDirty(true);
              }}
              renderList={({ children, props: listProps }) => (
                <div {...listProps} className="mb-4">
                  {children}
                </div>
              )}
              renderItem={({ value, props: itemProps, isDragged }) => (
                <div
                  {...itemProps}
                  key={value._key}
                  className="border rounded bg-white p-2 mb-2 d-flex align-items-center"
                  style={{ ...itemProps.style, opacity: isDragged ? 0.8 : 1 }}
                >
                  <span className="text-muted px-2">
                    <FontAwesomeIcon icon={faGripLines} />
                  </span>
                  {value.image ? (
                    <img
                      src={value.image}
                      alt=""
                      style={{ width: 52, height: 52, objectFit: "contain" }}
                      className="mr-3"
                    />
                  ) : (
                    <div
                      className="bg-light mr-3"
                      style={{ width: 52, height: 52 }}
                    />
                  )}
                  <div className="flex-grow-1">
                    <div className="font-weight-bold">{value.title}</div>
                    <div className="small text-muted">
                      <code>{value.handle}</code>
                      {value.sku ? ` · SKU ${value.sku}` : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeProduct(value._key);
                    }}
                    title="Remove"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              )}
            />
          )}
          <div className="d-flex align-items-center mb-5">
            <button
              type="button"
              className="btn btn-success px-4"
              disabled={!selectedProducts.length || props.loading()}
              onClick={save}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              Save {listType.label}
            </button>
            <button
              type="button"
              className="btn btn-outline-danger ml-2"
              disabled={!metaId || props.loading()}
              onClick={deleteMeta}
            >
              Delete metafield
            </button>
            <div className="small text-muted ml-3">
              Theme reads <code>collection.metafields.special_vendor.{listType.value}.value.products</code> in this exact order.
            </div>
          </div>
        </>
      ) : null}
      </> : null}
    </div>
  );
}
